import { type ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';

import { ApiError, useMeta } from '@/lib/api';
import { signOut, useAuthConfig } from '@/lib/auth';
import { cx } from '@/lib/cx';
import type { Theme } from '@/lib/theme';
import type { Meta } from '@/lib/types';

import { SignIn } from './SignIn';

const TABS = [
  { to: '/', label: 'Overview' },
  { to: '/feed', label: 'Feed' },
  { to: '/issues', label: 'Issues' },
  { to: '/demand', label: 'Demand' },
  { to: '/features', label: 'Features' },
  { to: '/accounts', label: 'Accounts' },
  { to: '/cost', label: 'Cost' },
  { to: '/journeys', label: 'Journeys' },
  { to: '/ask', label: 'Ask', ai: true },
  { to: '/digests', label: 'Digests' },
];

function age(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h`;
  return `${Math.round(minutes / 1440)} days`;
}

function syncLabel(sync: Meta['sync']): string {
  if (sync.state === 'never' || sync.age_minutes === null) return 'never synced';
  return `synced ${age(sync.age_minutes)} ago`;
}

function SyncBanner({ sync }: { sync: Meta['sync'] }) {
  const failing = sync.failing
    .map((f) => `${f.source}: ${f.consecutive_failures} failed (${f.last_error.slice(0, 80)})`)
    .join('; ');
  const text =
    sync.state === 'never' || sync.age_minutes === null
      ? 'No successful sync yet. Run `python -m usage sync` in compass-usage.'
      : `Data is ${age(sync.age_minutes)} old.`;
  return (
    <div className="banner" style={{ marginBottom: 0 }}>
      ⚠ {text}
      {failing && ` Failing: ${failing}.`}
    </div>
  );
}

function MessageCard({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="page">
      <div className="card empty">
        <div className="ic">🔒</div>
        <div style={{ fontWeight: 700, color: 'var(--ink)' }}>{title}</div>
        {children && <div>{children}</div>}
      </div>
    </div>
  );
}

export function Shell({ theme, onTheme }: { theme: Theme; onTheme: (theme: Theme) => void }) {
  const meta = useMeta();
  const data = meta.data;
  const authQuery = useAuthConfig();
  const auth = authQuery.data;
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const googleMode = auth?.mode === 'google';
  const notLocal = meta.error instanceof ApiError && meta.error.status === 401;
  const metaError = meta.error && !notLocal ? meta.error.message : null;
  const showSync = Boolean(data && data.sync.state !== 'fresh');
  const [bannerEl, setBannerEl] = useState<HTMLDivElement | null>(null);
  const bannerOn = !notLocal && (showSync || Boolean(metaError));
  useEffect(() => {
    const target = (bannerEl?.closest('.cu') ?? document.querySelector('.cu')) as HTMLElement | null;
    if (!target) return;
    if (!bannerEl) {
      target.style.setProperty('--banner-h', '0px');
      return;
    }
    const apply = () => target.style.setProperty('--banner-h', `${bannerEl.offsetHeight}px`);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(bannerEl);
    return () => observer.disconnect();
  }, [bannerEl]);
  return (
    <>
      <div className="top">
        <Link to="/" className="logo">
          <i />
          Compass Usage
        </Link>
        {!notLocal &&
          TABS.filter((t) => !t.ai || data?.ai_enabled).map((t) => (
            <NavLink key={t.to} to={t.to} end={t.to === '/'} className={({ isActive }) => cx(isActive && 'on')}>
              {t.label}
            </NavLink>
          ))}
        <div className="r">
          {data?.local_mode && <span className="pill neu">local mode</span>}
          {data && (
            <a
              className="sel"
              href={data.other_region?.url ?? undefined}
              title={data.other_region ? `Open ${data.other_region.label}` : 'One region'}
            >
              <span className="dot" style={{ background: 'var(--good)' }} />
              {data.region.toUpperCase()}
            </a>
          )}
          {data && (
            <span className={cx('faint sync', data.sync.state)} style={{ fontSize: 12 }}>
              {syncLabel(data.sync)}
            </span>
          )}
          <button
            type="button"
            className="iconbtn"
            title="Light / dark"
            onClick={() => onTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            {theme === 'dark' ? '☀' : '☾'}
          </button>
          {signOutError && <div role="alert">{signOutError}</div>}
          {googleMode && data && !notLocal && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                setSignOutError(null);
                signOut().then(
                  () => window.location.reload(),
                  (err: unknown) => setSignOutError(err instanceof Error ? err.message : "Couldn't sign out"),
                );
              }}
            >
              Sign out
            </button>
          )}
          {!notLocal && (
            <span className="avatar" title={data?.user.email}>
              {data?.user.initials ?? '··'}
            </span>
          )}
        </div>
      </div>
      {notLocal ? (
        authQuery.isPending ? null : authQuery.isError ? (
          <MessageCard title="Can't load sign-in. Try reloading the page." />
        ) : auth?.mode === 'google' ? (
          auth.google_client_id ? (
            <SignIn clientId={auth.google_client_id} />
          ) : (
            <MessageCard title="Sign-in isn't configured on this server." />
          )
        ) : auth?.mode === 'local' || auth?.mode === 'none' ? (
          <MessageCard title="The usage API refused the request">
            Start it in local mode: <code>USAGE_API_LOCAL_MODE=true pdm run python -m usage api</code>
          </MessageCard>
        ) : (
          <MessageCard title="The usage API refused the request" />
        )
      ) : (
        <>
          {bannerOn && (
            <div ref={setBannerEl} style={{ padding: '14px 20px 0' }}>
              {metaError ? (
                <div className="banner" style={{ marginBottom: 0 }}>
                  ⚠ {metaError}
                </div>
              ) : (
                data && <SyncBanner sync={data.sync} />
              )}
            </div>
          )}
          <Outlet />
        </>
      )}
    </>
  );
}
